import { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle, Lock } from 'lucide-react';

interface EmbeddedCheckoutProps {
  stripePublishableKey: string;
  clientSecret: string;
  stripeAccountId: string;
  amount: number;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onCancel: () => void;
}

function CheckoutForm({ amount, onPaymentSuccess, onCancel }: {
  amount: number;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);

  useEffect(() => {
    console.log('[EmbeddedCheckout] CheckoutForm mounted, stripe:', !!stripe, 'elements:', !!elements);
  }, [stripe, elements]);

  useEffect(() => {
    if (stripe) {
      console.log('[EmbeddedCheckout] Stripe instance available');
    }
  }, [stripe]);

  useEffect(() => {
    if (elements) {
      console.log('[EmbeddedCheckout] Elements instance available');
    }
  }, [elements]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isReady) {
        console.log('[EmbeddedCheckout] Load timed out after 20s. stripe:', !!stripe, 'elements:', !!elements, 'isReady:', isReady);
        setLoadTimedOut(true);
      }
    }, 20000);
    return () => clearTimeout(timer);
  }, [isReady]);

  const handleReady = useCallback(() => {
    console.log('[EmbeddedCheckout] PaymentElement onReady fired!');
    setIsReady(true);
  }, []);

  const handleLoadError = useCallback((event: any) => {
    console.error('[EmbeddedCheckout] PaymentElement onLoadError:', event);
    setStripeError(event?.error?.message || 'Payment element failed to load');
  }, []);

  const handleChange = useCallback((event: any) => {
    console.log('[EmbeddedCheckout] PaymentElement onChange:', event?.complete, event?.error?.message);
    if (event?.error) {
      setErrorMessage(event.error.message);
    } else {
      setErrorMessage(null);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isProcessing || paymentSucceeded) return;
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
    } else if (paymentIntent) {
      switch (paymentIntent.status) {
        case 'succeeded':
          setPaymentSucceeded(true);
          onPaymentSuccess(paymentIntent.id);
          break;
        case 'processing':
          setPaymentSucceeded(true);
          onPaymentSuccess(paymentIntent.id);
          break;
        case 'requires_action':
          setIsProcessing(false);
          break;
        default:
          setErrorMessage('Payment was not completed. Please try again.');
          setIsProcessing(false);
      }
    } else {
      setErrorMessage('Payment was not completed. Please try again.');
      setIsProcessing(false);
    }
  };

  if ((loadTimedOut || stripeError) && !isReady) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <AlertCircle className="w-6 h-6 text-destructive mb-3" />
        <p className="text-sm text-muted-foreground mb-3">
          {stripeError || 'Payment form could not load. Please close and try again.'}
        </p>
        <Button variant="outline" size="sm" onClick={onCancel} className="w-full">
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isReady && !loadTimedOut && !stripeError && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      )}
      <div className={!isReady ? 'invisible h-0 overflow-hidden' : ''}>
        <PaymentElement
          onReady={handleReady}
          onLoadError={handleLoadError}
          onChange={handleChange}
        />
      </div>
      {isReady && (
        <div className="pt-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
            <Lock className="w-3 h-3" />
            <span>Secured by Stripe</span>
          </div>
          {errorMessage && (
            <p className="text-sm text-destructive mb-3">{errorMessage}</p>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isProcessing || paymentSucceeded}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isProcessing || paymentSucceeded || !stripe || !elements}
              className="flex-1 bg-primary"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                `Pay $${amount.toFixed(2)}`
              )}
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}

export default function EmbeddedCheckout({
  stripePublishableKey,
  clientSecret,
  stripeAccountId,
  amount,
  onPaymentSuccess,
  onCancel,
}: EmbeddedCheckoutProps) {
  const [stripePromise] = useState(() => {
    console.log('[EmbeddedCheckout] Initializing loadStripe with key:', stripePublishableKey?.substring(0, 20) + '...', 'account:', stripeAccountId);
    console.log('[EmbeddedCheckout] clientSecret present:', !!clientSecret, 'length:', clientSecret?.length);
    // Eatzy now uses direct charges on the restaurant's connected Stripe account.
    // The PaymentIntent lives on the connected account (not the platform account), so we must
    // pass stripeAccount to loadStripe() so Stripe Elements looks in the right place.
    const promise = loadStripe(stripePublishableKey, {
      stripeAccount: stripeAccountId,
    });
    promise.then((stripe) => {
      console.log('[EmbeddedCheckout] loadStripe resolved:', stripe ? 'Stripe instance created' : 'NULL (failed to load)');
    }).catch((err) => {
      console.error('[EmbeddedCheckout] loadStripe rejected:', err);
    });
    return promise;
  });

  console.log('[EmbeddedCheckout] Rendering with clientSecret:', clientSecret ? clientSecret.substring(0, 30) + '...' : 'MISSING');

  return (
    <div className="min-h-[300px]">
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#ff9700',
              borderRadius: '6px',
            },
          },
        }}
      >
        <CheckoutForm
          amount={amount}
          onPaymentSuccess={onPaymentSuccess}
          onCancel={onCancel}
        />
      </Elements>
    </div>
  );
}
